import React from 'react';
import { isEmptyObject } from '../../../../utils/assertions';
import IconButton from '../../../atoms/iconButton/IconButton';
import { BinDeleteEmpty, BinDeleteStrike2 } from '../../../atoms/icons';
import PrimaryLoader from '../../../atoms/primaryLoader/PrimaryLoader';
import Price from '../../../molecules/price/Price';
import { Text } from '../../../typography/text/Text';
import OrderSummaryShippingLineItem from '../../checkout/orderSummary/OrderSummaryShippingLineItem';
import styles from './CartTotals.module.scss';
import { useGiftCardActions } from '../../../../utils/useGiftCardActions';
import { useCartContext } from '../../../../provider/cart/CartContext';
import { findOriginalIdDiscount } from '../../../../utils/helpers';
import clsx from 'clsx';

const CartTotals = ({
  subTotal,
  placement,
  shippingMessages,
  discounts,
  discountCodesOriginal,
  appliedVouchers,
  items,
}) => {
  const { removeGiftCard, loadingRemoveGift } = useGiftCardActions();
  const { removeDiscountFromCart, promoState, cartData } = useCartContext();

  const appliedVouchersArray =
    Object.entries(appliedVouchers).map(([key, value]) => ({
      key,
      value,
    })) || [];

  const handleRemoveDiscount = (originalId) => {
    if (!!discountCodesOriginal.length && originalId) {
      const id = findOriginalIdDiscount(originalId, discountCodesOriginal);
      id && removeDiscountFromCart(id);
    }
  };

  const RemoveButton = ({ onClickAction, loading }) => {
    return (
      <div className={loading ? styles.buttonIsLoad : ''}>
        <IconButton
          color="primary"
          iconRegular={() => <BinDeleteEmpty fill="#000" />}
          iconHover={() => <BinDeleteStrike2 fill="#000" />}
          iconActive={() => <BinDeleteStrike2 fill="#000" />}
          iconLoading={() => <PrimaryLoader mode="segment" size={14} />}
          loading={loading}
          onClick={onClickAction}
        />
      </div>
    );
  };

  const getPrice = (d) => {
    if (d.target === 'shipping' && d?.amount === 0) {
      const availableShippingMethods = cartData?.availableShippingMethods;
      const shippingMethodId = cartData?.cart?.shippingMethod?.shippingMethodId;

      const shippingMethod = availableShippingMethods.find(
        (m) => m?.shippingMethodId === shippingMethodId,
      );
      return shippingMethod?.rates.find((m) => m.price)?.price ?? d?.amount;
    }

    return d?.amount;
  };

  const getShippingCoastMessage = () => {
    if (
      discounts.find(
        (d) =>
          (d.target === 'shipping' && d.amount === 0) || !d?.isValidShipping,
      )
    ) {
      return null;
    }
    return shippingMessages;
  };

  return (
    <div className={styles[`totals_${placement}`]}>
      <div className={styles.summaryText}>
        <Text as="span" textTransform="firstletter" align="start">
          Subtotal:
        </Text>
        <Price
          placement="minicart"
          type="regular"
          value={subTotal}
          showCurrency
        />
      </div>
      <OrderSummaryShippingLineItem
        showCurrency={false}
        customClasses={{
          shippingBlock: styles.shippingBlock,
          minicartShippingText: styles.minicartShippingText,
        }}
        showSubtitle={!items.find((i) => i.type === 'gift-card')}
        shippingMessages={getShippingCoastMessage()}
      />
      {((discounts && !isEmptyObject(discounts) && discounts?.length !== 0) ||
        !!discounts.some((d) => d?.isValidShipping)) && (
        <Text as="span" className={styles.discountTitle}>
          Discount:
        </Text>
      )}

      {discounts
        ? !isEmptyObject(discounts) &&
          discounts.map((discount) => {
            if ((discount?.amount === 0 && discount?.target !== 'shipping') ||
              (!discount?.isValidShipping && discount?.target === 'shipping')) {
              return null;
            }

            return (
              <div
                key={discount.id}
                className={`${styles.summaryTextDiscount} ${styles.summaryHeight}`}
              >
                <div className={styles.promoDiscountBlock}>
                  <Text as="span">
                    {`${discount.name}${
                      discount?.description.startsWith('#') ||
                      !discount?.description
                        ? ''
                        : `, (${discount.description})`
                    }`}
                  </Text>

                  <div className={styles.discountBlock}>
                    <Text
                      as="div"
                      size="xs"
                      weight="light"
                      align="start"
                      className={styles.promoDiscountAmountDiscount}
                      showCurrency
                    >
                      -&nbsp;
                      <Price
                        placement="minicart"
                        type="regular"
                        value={getPrice(discount)}
                        showCurrency
                      />
                    </Text>
                    {!!findOriginalIdDiscount(
                      discount?.id,
                      discountCodesOriginal,
                    ) && (
                      <div className={styles.actions}>
                        <RemoveButton
                          onClickAction={() =>
                            handleRemoveDiscount(discount?.id)
                          }
                          loading={
                            promoState.loadingRemove &&
                            promoState.id ===
                              findOriginalIdDiscount(
                                discount?.id,
                                discountCodesOriginal,
                              )
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        : null}

      {appliedVouchersArray.length
        ? appliedVouchersArray.map((item, index) => (
            <div
              key={index}
              className={`${styles.summaryText} ${styles.summaryHeight}`}
            >
              <div
                className={clsx(styles.promoDiscount, styles.giftCardDiscount)}
              >
                <Text as="span" size="xs" weight="light" align="start">
                  Gift Card Applied:
                </Text>
              </div>
              <div>
                {item.value && (
                  <Text
                    as="div"
                    size="xs"
                    weight="light"
                    align="start"
                    className={styles.promoDiscountAmount}
                    showCurrency
                  >
                    -&nbsp;
                    <Price
                      placement="minicart"
                      type="regular"
                      value={item.value}
                      showCurrency
                    />
                  </Text>
                )}

                <div className={styles.actions}>
                  <RemoveButton
                    onClickAction={() => removeGiftCard(item.key)}
                    loading={loadingRemoveGift}
                  />
                </div>
              </div>
            </div>
          ))
        : null}
    </div>
  );
};

export default CartTotals;
